import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useState,
} from "react";

import type { Editor } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { CheckIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react";

import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";

import { useEditorContext } from "../editor/EditorContext";

const shouldShowFileToolbar = (
  editor: Editor,
  _state: EditorState,
  { requireFocus = true }: { requireFocus?: boolean } = {},
) => {
  if (editor.isDestroyed || !editor.isEditable) {
    return false;
  }
  // Match TipTap BubbleMenu default: only show when the editor is focused.
  if (requireFocus && !editor.isFocused) {
    return false;
  }
  return editor.isActive("file");
};

/**
 * Selection bubble for renaming / removing file attachments.
 * Mirrors FloatingLinkToolbar:
 * - selected file → name label + edit / remove
 * - edit → name input + accept / cancel
 */
export const FloatingFileToolbar = () => {
  const { editor, commands } = useEditorContext();
  // Non-null while the name field is open. Cleared by cancel / submit / hide.
  const [draft, setDraft] = useState<string | null>(null);

  const fileState = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return { name: "", canShow: false };
      }
      const name = (instance.getAttributes("file").name as string) || "";
      // Ignore focus here: while renaming, focus moves to the input and
      // clearing draft would abort edit mode.
      return {
        name,
        canShow: shouldShowFileToolbar(instance, instance.state, {
          requireFocus: false,
        }),
      };
    },
  });

  const name = fileState?.name ?? "";
  const canShow = fileState?.canShow ?? false;

  // Exit edit mode when the selection leaves the file node.
  if (draft !== null && !canShow) {
    setDraft(null);
  }

  const isEditing = draft !== null && canShow;

  // Stable identity so Semantic UI Input only focuses on mount, not every keystroke.
  const focusInput = useCallback((node: HTMLInputElement | null) => {
    if (!node) {
      return;
    }
    node.focus({ preventScroll: true });
    node.select();
  }, []);

  const startEditing = () => {
    setDraft(name);
  };

  const cancelEditing = () => {
    setDraft(null);
    editor?.commands.focus();
  };

  const submitName = () => {
    const trimmed = (draft ?? "").trim();
    setDraft(null);
    if (!trimmed || trimmed === name) {
      editor?.commands.focus();
      return;
    }
    commands.updateFile?.({ name: trimmed });
  };

  const removeFile = () => {
    commands.removeFile?.();
    setDraft(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitName();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      key="floating-file-toolbar"
      editor={editor}
      updateDelay={0}
      style={{ zIndex: 11, background: "#fff" }}
      options={{ placement: "top-start", offset: 4 }}
      shouldShow={({ editor: instance, state }) => {
        if (isEditing) {
          return true;
        }
        return shouldShowFileToolbar(instance, state);
      }}
    >
      {isEditing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Input
            ref={focusInput}
            type="text"
            value={draft ?? ""}
            placeholder="Enter file name..."
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            size="small"
            style={{ width: 280 }}
          />
          <ButtonGroup variant="outline" size="small">
            <Button
              type="button"
              aria-label="Apply file name"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={submitName}
              variant="outline"
            >
              <Icon icon={CheckIcon} />
            </Button>
            <Button
              type="button"
              aria-label="Cancel"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={cancelEditing}
              variant="outline"
            >
              <Icon icon={XIcon} />
            </Button>
          </ButtonGroup>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <ButtonGroup size="small">
            <Button
              aria-label="Rename file"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={startEditing}
              variant="outline"
            >
              <Icon icon={PencilIcon} />
            </Button>
            <Button
              aria-label="Remove file"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={removeFile}
              variant="outline"
            >
              <Icon icon={Trash2Icon} />
            </Button>
          </ButtonGroup>
        </div>
      )}
    </BubbleMenu>
  );
};
